import { useMemo } from 'react';

import { getFilterValues, isFilterExcluded, useTableFilters } from '@/hooks/useTableFilters';
import { AIAgent, BrainType } from '@/shared/types/brain';

/**
 * Hook to manage AI Agents page filtering with saved filter views.
 * Combines generic filters with AI Agents-specific filtering logic.
 */
export const useAIAgentsFilters = (aiAgents: AIAgent[] | undefined) => {
  const {
    filters,
    setFilter,
    removeFilter,
    resetFilters,
    activeFilterCount,
    savedViews,
    activeView,
    hasUnsavedChanges,
    saveView,
    updateView,
    deleteView,
    renameView,
    applyView,
    clearActiveView,
  } = useTableFilters({ storageKey: 'aiAgentsFilters', preferencesKey: 'ai_agents' });

  const filteredAIAgents = useMemo(() => {
    if (!aiAgents?.length) {
      return [];
    }

    const types = getFilterValues(filters, 'types') as BrainType[];
    const languages = getFilterValues(filters, 'languages');
    const languageExcluded = isFilterExcluded(filters, 'languages');

    return aiAgents.filter((agent) => {
      if (types.length && !types.includes(agent.brain_type)) {
        return false;
      }

      if (languages.length) {
        const matchesLanguage = languages.includes(agent.language);
        if (languageExcluded ? matchesLanguage : !matchesLanguage) {
          return false;
        }
      }

      return true;
    });
  }, [aiAgents, filters]);

  return {
    filters,
    activeFilterCount,
    setFilter,
    removeFilter,
    resetFilters,
    filteredAIAgents,
    savedViews,
    activeView,
    hasUnsavedChanges,
    applyView,
    clearActiveView,
    saveView,
    renameView,
    updateView,
    deleteView,
  };
};
